import * as React from "react";
import "./DynProvisionProgressDlg.scss";
import Api from "./Api";
import Dialog from "./input/Dialog";
import { IDynProvision } from "@testout/testout-commerce/models/DynProvision";
import { EmailArray } from "@testout/testout-commerce/models/Email";
import { ListItem } from "@testout/testout-commerce/models/ListItem";
import { User } from "@testout/testout-commerce/models/labsim/User";
import { Reservation, ReservationType } from "@testout/testout-commerce/models/labsim/Reservation";
import { DynContact } from "@testout/testout-commerce/models/dynamics/DynContact";
import { ContactName } from "@testout/testout-commerce/models/ContactName";

interface DynProvisionProgressProps {
    provision: IDynProvision;
    products: ListItem[],
    env?: string,
    isOpen: boolean;
    onClose: (failed: boolean) => void;
}

interface DynProvisionProgressState {
    percent: number;
    messages: string[];
    disableClose: boolean;
}

export default class DynProvisionProgressDlg extends React.PureComponent<DynProvisionProgressProps, DynProvisionProgressState> {
    constructor(props: DynProvisionProgressProps) {
        super(props);
        this.state = { messages: [], percent: 0, disableClose: true };
    }

    _scrollerRef = React.createRef<HTMLDivElement>();

    onOpen = async () => {
        const { provision, products } = this.props;

        try {
            this.setState({ messages: [], percent: 0, disableClose: true });

            // Determine the task size for progress bar
            const counts = provision.accounts.map(account => new EmailArray(account.emails).length);
            const taskPercent = 100 / (1 + counts.length + counts.reduce((sum, count) => sum + count, 0) * 10);
            let percent = 0;

            // Build the list of courses from the selected products
            const productIds: number[] = provision.products.map((productNumber: string) => Number.parseInt(products.find((item) => item.value === productNumber)?.["data"]?.["courseId"] ?? "0"));

            for (const account of provision.accounts) {
                percent = await this.provisionAccount(account, provision, productIds, taskPercent, percent);
            }

            this.setState(prevState => ({
                disableClose: false,
                messages: [...prevState.messages, prevState.messages.some(message => message.startsWith("*"))
                    ? `*Provisioning completed with errors.`
                    : `Provisioning complete.`]
            }));
        }
        catch (err: any) {
            this.setState(prevState => ({ disableClose: false, messages: [...prevState.messages, `*${err.message}`] }));
        }
    }

    private async provisionAccount(
        account: any,
        provision: IDynProvision,
        productIds: number[],
        taskPercent: number,
        initialPercent: number
    ): Promise<number> {
        let percent = initialPercent;

        this.addMessage(`Associating ${account.name} to CertMaster Institution.`);
        await Api.patch(`/api/dynamics/accounts/${account.accountId}?env=${this.props.env || ""}`, 
            { comptia_labsimcampusprofileid: account.campusProfileId });
        percent += taskPercent;
        this.setState({ percent });

        if (provision.useInstructorLicense) {
            percent = await this.provisionInstructorLicense(account, provision, productIds, taskPercent, percent);
        }
        percent += taskPercent;
        this.setState({ percent });

        for (const email of new EmailArray(account.emails)) {
            try {
                percent = await this.provisionInstructor(email, account, provision, productIds, taskPercent, percent);
            } catch (error: any) {
                this.addMessage(`*${error.message}`);
            }
        }

        return percent;
    }

    private async provisionInstructorLicense(
        account: any,
        provision: IDynProvision,
        productIds: number[],
        taskPercent: number,
        percent: number
    ): Promise<number> {
        this.addMessage(` Provisioning instructor licenses...`);
        const reservations = await Api.get<Reservation[]>(
            `/api/campuses/${account.campusProfileId}/reservations?reservationType=InstructorLicense&description=Instructor License`
        );
        const reservation = reservations.find(r => !r.isDeleted && r.name === "Instructor License");

        if (reservation) {
            reservation.productIds = Array.from(new Set([...reservation.productIds, ...productIds]));
            if (provision.expirationDate && reservation.expirationDate && reservation.expirationDate > provision.expirationDate)
                reservation.expirationDate = provision.expirationDate;
            await Api.put(`/api/reservations`, reservation);
        } else {
            await Api.post<Reservation>(`/api/reservations`, {
                reservationType: ReservationType.InstructorLicense,
                productIds: productIds,
                expirationDate: provision.expirationDate,
                isCampusRequired: true,
                campusId: account.campusProfileId,
                name: "Instructor License",
                allowedActivationCount: 0
            });
        }

        return percent;
    }

    private async provisionInstructor(
        email: any,
        account: any,
        provision: IDynProvision,
        productIds: number[],
        taskPercent: number,
        initialPercent: number
    ): Promise<number> {
        let percent = initialPercent;

        this.addMessage(`Setting up instructor ${email.toString()}.`);

        const contact = await this.getOrCreateContact(email, account, taskPercent, percent);
        percent = contact.percent;

        if (provision.opportunityId) {
            await Api.put(`/api/dynamics/opportunities/${provision.opportunityId}/contacts/${contact.data.contactid}?env=${this.props.env || ""}`, "PUT");
        }
        percent += taskPercent;
        this.setState({ percent });

        const user = await this.getOrCreateUser(email, contact.data, taskPercent, percent);
        percent = user.percent;

        await Api.post(`/api/users/${user.data.userProfileId}/campuses/${account.campusProfileId}?isInstructor=true&isAdmin=${account.makeTeachersAdmins}`);
        percent += taskPercent;
        this.setState({ percent });

        const reservation = await this.provisionAccess(user.data, account, provision, productIds, taskPercent, percent);
        percent = reservation.percent;

        percent = await this.sendWelcomeEmail(contact.data, user.data, account, provision, reservation.data, taskPercent, percent);

        return percent;
    }

    private async getOrCreateContact(
        email: any,
        account: any,
        taskPercent: number,
        initialPercent: number
    ): Promise<{ data: DynContact; percent: number }> {
        let percent = initialPercent;

        let contact = await Api.get<DynContact>(
            `/api/dynamics/contacts?email=${encodeURIComponent(email.address)}&env=${this.props.env || ""}`
        );
        percent += taskPercent;
        this.setState({ percent });

        if (!contact) {
            const person = this.createPersonName(email);
            contact = await Api.post<DynContact>(`/api/dynamics/contacts?env=${this.props.env || ""}`, {
                emailaddress1: email.address,
                fullname: person.toString(),
                parentcustomerid: account.accountId
            });
        }
        percent += taskPercent;
        this.setState({ percent });

        return { data: contact, percent };
    }

    private createPersonName(email: any): ContactName {
        const person = new ContactName(email.displayName || email.localPart);
        if (!person.firstNames)
            person.firstNames = (person.lastName?.length ?? 0) > 0 ? person.lastName?.[0] : "unknown";
        if (!person.lastName)
            person.lastName = "unknown";
        return person;
    }

    private async getOrCreateUser(
        email: any,
        contact: DynContact,
        taskPercent: number,
        initialPercent: number
    ): Promise<{ data: User; percent: number }> {
        let percent = initialPercent;

        this.addMessage(` Provisioning user...`);

        let user: User | undefined;
        if (contact.comptia_labsimuserprofileid) {
            user = await this.findExistingUser(contact.comptia_labsimuserprofileid);
            if (!user) {
                contact.comptia_labsimuserprofileid = "";
            } else if (user.userProfileId && user.userProfileId.toLowerCase() !== contact.comptia_labsimuserprofileid.toLowerCase()) {
                this.addMessage(` Contact was linked to a merged user; updating to ${user.userProfileId}.`);
                await Api.patch(`/api/dynamics/contacts/${contact.contactid}?env=${this.props.env || ""}`,
                    { comptia_labsimuserprofileid: user.userProfileId });
                contact.comptia_labsimuserprofileid = user.userProfileId;
            }
        }
        percent += taskPercent;
        this.setState({ percent });

        if (contact.comptia_labsimuserprofileid) {
            percent += 2 * taskPercent;
            this.setState({ percent });
        } else {
            const person = this.createPersonName(email);
            user = await Api.post<User>("/api/users", {
                givenName: "",
                surname: "",
                signOns: [{ systemId: 0, email: email.address }],
                fullName: person.toString()
            });
            percent += taskPercent;
            this.setState({ percent });

            if (!user)
                throw new Error("User could not be created");

            await Api.patch(`/api/dynamics/contacts/${contact.contactid}?env=${this.props.env || ""}`, 
                { comptia_labsimuserprofileid: user.userProfileId });
            percent += taskPercent;
            this.setState({ percent });
        }

        if (!user)
            throw new Error("User could not be found or created");

        return { data: user, percent };
    }

    private async findExistingUser(userProfileId: string): Promise<User | undefined> {
        try {
            return await Api.get<User>(`/api/users/${userProfileId}`);
        } catch (userError: any) {
            if (/\b404\b/.test(userError.message ?? ""))
                return undefined;
            throw userError;
        }
    }

    private async provisionAccess(
        user: User,
        account: any,
        provision: IDynProvision,
        productIds: number[],
        taskPercent: number,
        initialPercent: number
    ): Promise<{ data: Reservation | undefined; percent: number }> {
        let percent = initialPercent;
        let reservation: Reservation | undefined;

        if (provision.useInstructorLicense) {
            percent += 2 * taskPercent;
            this.setState({ percent });
        } else {
            this.addMessage(` Provisioning CertMaster course access...`);
            reservation = await Api.post<Reservation>(`/api/reservations`, {
                reservationType: ReservationType.ActivationCode,
                productIds: productIds,
                expirationDate: provision.expirationDate,
                isCampusRequired: true,
                campusId: account.campusProfileId,
                allowedActivationCount: 1
            });
            percent += taskPercent;
            this.setState({ percent });

            await Api.post(`/api/users/${user.userProfileId}/activations/${reservation.reservationId}/1`);
            percent += taskPercent;
            this.setState({ percent });
        }

        return { data: reservation, percent };
    }

    private async sendWelcomeEmail(
        contact: DynContact,
        user: User,
        account: any,
        provision: IDynProvision,
        reservation: Reservation | undefined,
        taskPercent: number,
        initialPercent: number
    ): Promise<number> {
        let percent = initialPercent;

        this.addMessage(` Sending welcome email...`);
        const productList = provision.products.map(productNumber =>
            this.props.products.find(item => item.value === productNumber)?.["name"] ?? ""
        );

        await Api.post(`/api/dynamics/contacts/${contact.contactid}/welcome?env=${this.props.env || ""}`, {
            userProfileId: user.userProfileId,
            campusProfileId: account.campusProfileId,
            reservationId: reservation?.reservationId,
            accountId: account.accountId,
            email: contact.emailaddress1,
            firstname: contact.firstname,
            lastname: contact.lastname,
            login: (user.signOns ?? []).find(signOn => signOn.systemId === "0")?.["userKey"],
            password: (user.signOns ?? []).find(signOn => signOn.systemId === "0")?.["tempPassword"],
            productlist: this.formatList(productList)
        });
        percent += taskPercent;
        this.setState({ percent });

        return percent;
    }

    private addMessage(message: string): void {
        this.setState(prevState => ({ 
            messages: [...prevState.messages, message] 
        }));
    }

    onClose = () => {
        this.props.onClose(this.state.messages.some(message => message.startsWith("*")));
    }
    
    private formatList(n: string[]): string {
        if (n.length === 0) return '';
        if (n.length === 1) return n[0];
        if (n.length === 2) return n.join(" and ");
        return `${n.slice(0, -1).join(", ")}, and ${n.slice(-1)}`;
    }

    private getMessageClassName(message: string): string {
        if (message.startsWith(" ")) {
            return "indent";
        } else if (message.startsWith("*")) {
            return "error";
        }
        return "";
    }

    scrollToBottom() {
        if (this.props.isOpen && this._scrollerRef.current) {
            const divScroller = this._scrollerRef.current;
            divScroller.scrollTop = divScroller.scrollHeight;
        }
    }

    componentDidMount() {
        this.scrollToBottom();
    }

    componentDidUpdate() {
        this.scrollToBottom();
    }

    render() {
        return <Dialog title="Provision Progress" className="provision-dialog" isOpen={this.props.isOpen}
                       onOpen={this.onOpen} disableClose={this.state.disableClose} onClose={this.onClose}>
                   <div className="progress-bar">
                       <div style={{ width: `${this.state.percent}%` }}></div></div>
                   <div className="messages scroller" ref={this._scrollerRef}>
                       <ul>
                           {this.state.messages.map(
                               (message, i) => <li key={i} className={this.getMessageClassName(message)}> {/* NOSONAR */}
                                   {message.startsWith("*") ? message.substring(1) : message}
                               </li>)}
                       </ul>
                   </div>
                   <footer>
                       <button className="primary" disabled={this.state.disableClose}
                               onClick={this.onClose}>Close</button></footer>
               </Dialog>;
    }
}